/* eslint-disable @typescript-eslint/no-use-before-define */
import { ActionButtonProps } from '@euler/app/components/BottomAction';
import { SuspenseContentWrapper } from '@euler/app/components/SuspenseContentWrapper';
import {
  getReportResourceId,
  ReportRef,
  useReportData,
} from '@euler/app/flows/report/functions';
import { useReportHeaderMetrics } from '@euler/app/flows/report/functions/useReportHeaderMetrics';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { VehicleReport, VehicleReportProjection } from '@euler/model/report';
import { ReportTabKey } from '@euler/model/viewmodel';
import {
  createContext,
  memo,
  ReactNode,
  RefObject,
  useContext,
  useMemo,
} from 'react';
import { StyleProp, TouchableOpacity, ViewStyle } from 'react-native';

export type ReportScreenProps = {
  target: ReportRef;
  title?: string;
  subTitle?: string | ((report: VehicleReport) => string | undefined);
  accessoryText?: string;
  standalone?: boolean;
  action?: ActionButtonProps;
  secondaryAction?: ActionButtonProps;
  ipadHeaderStyle?: StyleProp<ViewStyle>;
  ipadFooterStyle?: StyleProp<ViewStyle>;
  shareButtonRef?: () => RefObject<TouchableOpacity>;
  onShare?: (tab?: ReportTabKey) => void;
  flowId?: string;
};

export type ReportContextProps = {
  projection: VehicleReportProjection;
  report: VehicleReport;
  isRefreshing: boolean;
  offsetTop: number;
  refresh: () => void;
  error?: Error;
};

export const ReportContext = createContext<ReportContextProps>(null as any);

export const useReportContext = () => {
  return useContext(ReportContext);
};

type ReportContextProviderProps = {
  offsetTop: number;
  target: ReportRef;
  projection: VehicleReportProjection;
  children?: ReactNode;
  suspenseFallback?: ReactNode;
};

export const ReportContextProvider = memo(
  ({ suspenseFallback, ...props }: ReportContextProviderProps) => {
    const { rpx } = useResponsiveContext();
    const { navBarHeight } = useReportHeaderMetrics();
    const resourceId = getReportResourceId(props.target, props.projection);
    return (
      <SuspenseContentWrapper
        resourceId={resourceId}
        resourceType="async"
        fallback={suspenseFallback}
        contentContainerStyle={{
          paddingTop: navBarHeight + rpx(32),
        }}
      >
        <Content {...props} />
      </SuspenseContentWrapper>
    );
  },
);

const Content = memo(
  ({ offsetTop, target, projection, children }: ReportContextProviderProps) => {
    const { report, refresh, isRefreshing, error } = useReportData(
      target,
      projection,
    );
    const value = useMemo<ReportContextProps>(
      () => ({
        offsetTop,
        report,
        projection,
        isRefreshing,
        refresh,
        error,
      }),
      [error, isRefreshing, offsetTop, projection, refresh, report],
    );

    return (
      <ReportContext.Provider value={value}>{children}</ReportContext.Provider>
    );
  },
);
